<template>
  <div class="subscribe-block">
    <template v-if="showIsNotLoggedIn">
      <p>Logga in och börja prenumerera på Inspiration från ICA redan idag.</p>

      <button
        class="button--40"
        type="button"
        @click="showLoginModal"
      >
        Logga in
      </button>
    </template>

    <template v-if="showIsSubscribing">
      <div class="subscribe-block__icon">
        <svgicon
          name="Illustrative/Quality"
          :original="true"
        />
        <p class="subscribe-block__hooray">
          Hurra, du prenumererar redan på Inspiration från ICA!
        </p>
      </div>
      <p>Vi skickar mejlet till {{ email }}. Hittar du inte det? Kolla din skräppost om det inte ligger där.</p>
      <p>
        Under
        <a
          class="link"
          href="https://www.ica.se/mitt-konto"
        >dina inställningar</a> kan du uppdatera din mejladress. Där kan du också ändra vilka mejl du får.
      </p>
    </template>

    <template v-if="showIsNotSubscribing">
      <p>Kom igång och börja prenumerera på Inspiration från ICA redan idag.</p>

      <subscribe-module class="subscribe-block__email">
        <template slot="header">
          E-post
        </template>
        <EditButton
          slot="editButton"
          :editing="!emailEdit"
          @toggleEdit="toggleEditEmail"
        />
        <template
          v-if="!emailEdit"
          slot="info"
        >
          {{ emailAddress }}
        </template>
        <p
          v-if="!emailAddress"
          slot="info"
          class="missing-info"
        >
          Vänligen registrera en e-postadress!
        </p>
        <template
          v-if="emailEdit"
          slot="info"
        >
          <EditMode
            @cancelEditMode="toggleEditEmail"
            @saveEditMode="trackEvent($route, 'cpcProfilEmail'); updateEmail()"
          >
            <ica-input-text
              slot="input"
              v-model="emailAddress"
              :type="'email'"
              :value="emailAddress"
              :label="'E-post'"
              :placeholder="'Ange en e-post'"
              :name="'email'"
              :size="'48'"
              :required="true"
              :custom-validate-func="isValidEmail"
            />
          </EditMode>

          <p
            class="subscribe-block__edit-notice"
          >
            När du ändrar mejladress här, byter du e-postadress på hela kontot.
          </p>
        </template>

        <success-edit-info
          v-if="successEdit"
          slot="errorMessage"
          :success-info-text="successInfoText"
        />
        <error-missing-info
          v-if="errorEdit"
          slot="errorMessage"
          :missing-info-text="errorMessageText"
        />
      </subscribe-module>
      <button
        class="button--40 subscribe-block__subscribe"
        type="button"
        :disabled="isSubscribeDisabled"
        @click="subscribe"
      >
        Börja prenumerera
      </button>

      <transition name="subscribe-block__fade">
        <technical-error
          v-if="showSubscribeError"
          class="subscribe-block__subscribe-error"
        />
      </transition>

      <p class="subscribe-block__post-message">
        Du kan enkelt ändra vilka mejl du får. Gå till
        <a
          class="link"
          href="https://www.ica.se/mitt-konto"
        >dina inställningar</a> eller klicka på länken längst ner i ett mejl för att sluta prenumerera på det.
      </p>
      <p>
        <a
          href="/policies/behandling-av-personuppgifter/"
          class="link subscribe-block__read-more"
        >Läs mer om hur ICA hanterar dina personuppgifter.</a>
      </p>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import * as EmailValidator from 'email-validator'
import EditButton from './EditButton'
import EditMode from './EditMode'
import SubscribeModule from './SubscribeModule'
import IcaInputText from '~/@ica/ica-molecules-vue/dist/InputText'
import ErrorMissingInfo from './ErrorMissingInfo'
import SuccessEditInfo from './SuccessEditInfo'

import { service } from '@/services/'
import '#/Illustrative/Quality'
import dataLayer from '@/analytics/dataLayer.js'

export default {
  name: 'SubscribeBlock',
  components: {
    EditButton,
    EditMode,
    ErrorMissingInfo,
    IcaInputText,
    SubscribeModule,
    SuccessEditInfo
  },
  props: {
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    email: {
      type: String,
      default: ''
    },
    isSubscribingCentralMails: {
      type: Boolean,
      default: false
    },
    newRelicContext: {
      type: String,
      required: false,
      default: 'SubscribeBlock'
    }
  },
  data () {
    return {
      disableSubscribeButton: false,
      emailAddress: null,
      emailEdit: false,
      errorMessageText: '',
      errorEdit: false,
      showSubscribeError: false,
      successEdit: false,
      successInfoText: 'Dina ändringar har sparats.',
      showIsSubscribing: false
    }
  },
  computed: {
    isSubscribeDisabled () {
      return this.disableSubscribeButton
    },
    showIsNotLoggedIn () {
      return !this.isLoggedIn
    },
    showIsNotSubscribing () {
      return this.isLoggedIn && !this.isSubscribingCentralMails
    }
  },
  async mounted () {
    await this.getPublicAccessToken()

    this.emailAddress = this.email
    if (!this.emailAddress) {
      this.emailEdit = true
    }

    this.initShowIsSubscribing()
  },
  methods: {
    ...mapActions(['getPublicAccessToken']),
    isValidEmail (_email) {
      if (!_email) {
        return EmailValidator.validate(this.emailAddress)
      }
      return EmailValidator.validate(_email)
    },
    NRLog (msg) {
      // New relic logging
      !!window.NREUM && window.NREUM.noticeError(msg)
    },
    showLoginModal () {
      try {
        window.icaseUserLogin(this.onClose)
      } catch (err) {
        console.log(err)
        const msg = `${this.newRelicContext} - Could not load modal: ${err.message}`
        this.NRLog(msg)
      }
    },
    initShowIsSubscribing () {
      if (this.isSubscribingCentralMails === null) {
        this.showSubscribeError = true
        // this.disableSubscribeButton = true
        this.showIsSubscribing = false
      } else {
        this.showIsSubscribing = this.isLoggedIn && this.isSubscribingCentralMails
      }
    },
    subscribe () {
      this.disableSubscribeButton = true
      return service
        .subscribe()
        .then(() => {
          dataLayer.add({
            event: 'cpcAddEmail',
            cpcVpvUrl: 'utskick-fran-ica'
          })
          window.setTimeout(() => {
            window.location.href = '/utskick/prenumeration-bekraftad'
          }, 100)
        })
        .catch(err => {
          console.log(err)
          this.disableSubscribeButton = false
          this.showSubscribeError = true
        })
    },
    toggleEditEmail () {
      this.emailEdit = !this.emailEdit
      if (this.emailEdit) {
        this.successEdit = false
        this.errorEdit = false
      }
    },
    trackEvent () {
      dataLayer.add({
        event: 'cpcProfilEmail',
        cpcVpvUrl: 'utskick-fran-ica'
      })
    },
    async updateEmail () {
      const serviceResponse = await service
          .updateEmail({
            baseUrl: this.$root.envConfig.customerDataApiBaseUrl,
            email: this.emailAddress,
            publicAccessToken: await this.getPublicAccessToken()
          })

      if(serviceResponse.success) {
          this.emailEdit = false
          this.errorEdit = false
          this.successEdit = true
      }

      if(!serviceResponse.success) {
        this.errorMessageText = serviceResponse.message
        this.successEdit = false
        this.errorEdit = true
        this.emailEdit = true
      }
    }
  }
}
</script>

<style lang="scss">
.subscribe-block {
  padding: 0 1.5rem 1.5rem 1.5rem;
  margin-bottom: 0.5rem;

  @include media-breakpoint-up(md) {
    padding: 0 2.5rem 2rem 2.5rem;
  }

  background-color: $color-white;

  &__p {
    @include font(text, 3, "normal");
  }

  &__read-more {
    display: block;
    margin: 2rem 0;
  }

  &__icon {
    text-align: center;

    svg {
      display: block;
      width: 200px;
      height: 200px;
      margin: 36px auto 8px;
    }
  }

  &__hooray {
    font-family: 'ICA Hand', serif;
    font-weight: 400;
    font-size: 1.5rem;
    // line-height: 0.8rem;
    letter-spacing: 0;
    color: $color-pink-mid;
    margin: 0 0 56px;
  }

  &__email {
    margin: 36px 0;
  }

  &__edit-notice {
    @include font(text, 3, "bold");

    font-style: italic;
  }

  &__email-invalid,
  &__subscribe-error {
    margin: 2rem 0;
  }

  &__fade-enter-active {
    transition: opacity 0.5s;
  }

  &__fade-enter {
    opacity: 0;
  }
}
</style>
